import { createContext, useContext, useEffect, useState, useRef, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { User, AuthChangeEvent } from "@supabase/supabase-js";
import { setSentryUserContext } from "@/integrations/sentry";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { isNetworkError } from "@/lib/errorHandling";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { WifiOff, RefreshCw, AlertCircle } from "lucide-react";

// Stale time: données considérées fraîches pendant 5 minutes
const STALE_TIME_MS = 5 * 60 * 1000;

interface Organization {
  id: string;
  name: string;
  code: string;
  email: string | null;
  reward_threshold: number;
  suivi_temperature: boolean;
  depot_especes: boolean;
  horaires_google: boolean;
  google_schedules_planning_enabled: boolean;
  gestion_commandes_sec: boolean;
  suivi_lots: boolean;
  inventaires: boolean;
  avis_google: boolean;
  buches: boolean;
  google_location_id: string | null;
  org_type: string;
  latitude: number | null;
  longitude: number | null;
  created_at: string;
}

interface Membership {
  organization_id: string;
  role: string;
  status: string;
  created_at: string;
}

interface Profile {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  active_organization_id: string | null;
}

interface FeatureFlags {
  temperatureTracking: boolean;
  cashDeposits: boolean;
  googleSchedules: boolean;
  googleSchedulesPlanning: boolean;
  dryGoodsManagement: boolean;
  lotTracking: boolean;
  inventories: boolean;
  googleReviews: boolean;
  buches: boolean;
}

interface OrganizationContextType {
  // Core data
  user: User | null;
  profile: Profile | null;
  organization: Organization | null;
  membership: Membership | null;

  // Convenience properties
  role: string;
  organizationId: string;
  isHeadquarters: boolean;

  // Feature flags
  features: FeatureFlags;

  // State
  loading: boolean;           // true uniquement au chargement initial
  isRefreshing: boolean;      // true pendant un refresh silencieux en arrière-plan
  error: { isNetwork: boolean; message: string } | null;

  // Helper function
  isFeatureEnabled: (feature: keyof FeatureFlags) => boolean;

  // Refresh function for when data changes
  refresh: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

interface OrganizationProviderProps {
  children: ReactNode;
}

export function OrganizationProvider({ children }: OrganizationProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [membership, setMembership] = useState<Membership | null>(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<{ isNetwork: boolean; message: string } | null>(null);
  const lastFetchTimeRef = useRef<number | null>(null);
  const isOnline = useNetworkStatus();

  const loadOrganizationData = async (isRefresh = false) => {
    // Skip si refresh et données fraîches (moins de 5 minutes)
    if (isRefresh && lastFetchTimeRef.current && Date.now() - lastFetchTimeRef.current < STALE_TIME_MS) {
      return;
    }

    // Ne montrer le loading que pour le chargement initial
    if (!isRefresh) {
      setInitialLoading(true);
    } else {
      setIsRefreshing(true);
    }

    setError(null);

    try {
      // Get current user session
      const {
        data: { session },
      } = await supabase.auth.getSession();

      if (!session) {
        setUser(null);
        setProfile(null);
        setOrganization(null);
        setMembership(null);
        if (!isRefresh) setInitialLoading(false);
        setIsRefreshing(false);
        return;
      }

      setUser(session.user);

      // Get user's profile and organization membership in parallel
      const [{ data: profileData }, { data: membershipData }] = await Promise.all([
        supabase
          .from("profiles")
          .select("*")
          .eq("id", session.user.id)
          .single(),
        supabase
          .from("organization_members")
          .select("organization_id, role, status, created_at")
          .eq("user_id", session.user.id)
          .eq("status", "approved")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle(),
      ]);

      if (profileData) {
        setProfile(profileData);
      }

      if (!membershipData) {
        setMembership(null);
        setOrganization(null);
        lastFetchTimeRef.current = Date.now();
        if (!isRefresh) setInitialLoading(false);
        setIsRefreshing(false);
        return;
      }

      setMembership(membershipData);

      // Get organization details
      const { data: orgData } = await supabase
        .from("organizations")
        .select("*")
        .eq("id", membershipData.organization_id)
        .single();

      if (orgData) {
        setOrganization(orgData);
      }

      lastFetchTimeRef.current = Date.now();
      if (!isRefresh) setInitialLoading(false);
      setIsRefreshing(false);
    } catch (err: any) {
      console.error("Error loading organization data:", err);

      // Detect network errors
      if (isNetworkError(err)) {
        setError({
          isNetwork: true,
          message: "Impossible de charger les données. Vérifiez votre connexion.",
        });
      } else {
        setError({
          isNetwork: false,
          message: "Une erreur est survenue lors du chargement.",
        });
      }

      if (!isRefresh) setInitialLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    loadOrganizationData();

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event: AuthChangeEvent, session) => {
      // Ignorer TOKEN_REFRESHED - c'est l'événement qui cause le flash au retour d'onglet
      // On ne veut refetcher que sur des changements significatifs d'état auth
      if (event === 'TOKEN_REFRESHED') {
        return;
      }

      if (session?.user) {
        // Lors d'un login, forcer le rechargement complet (ignorer le cache de fraîcheur)
        // car l'utilisateur a changé et les données précédentes ne sont plus valides
        lastFetchTimeRef.current = null;
        loadOrganizationData(true);
      } else {
        // Déconnexion : reset immédiat
        setUser(null);
        setProfile(null);
        setOrganization(null);
        setMembership(null);
        setInitialLoading(false);
        setIsRefreshing(false);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  // Auto-retry when network comes back online
  useEffect(() => {
    if (isOnline && error?.isNetwork) {
      loadOrganizationData();
    }
  }, [isOnline]);

  // Contexte Sentry : chaque erreur remontée porte le compte et le salon concernés.
  useEffect(() => {
    if (!user) {
      setSentryUserContext(null);
      return;
    }
    const name = [profile?.first_name, profile?.last_name].filter(Boolean).join(" ");
    setSentryUserContext({
      userId: user.id,
      email: user.email ?? profile?.email,
      name: name || undefined,
      organization,
      role: membership?.role,
    });
  }, [user, profile, organization, membership]);

  // Compute feature flags from organization data
  const features: FeatureFlags = {
    temperatureTracking: organization?.suivi_temperature ?? false,
    cashDeposits: organization?.depot_especes ?? true,
    googleSchedules: organization?.horaires_google ?? false,
    googleSchedulesPlanning:
      (organization?.horaires_google ?? false) &&
      (organization?.google_schedules_planning_enabled ?? false),
    dryGoodsManagement: organization?.gestion_commandes_sec ?? false,
    lotTracking: organization?.suivi_lots ?? false,
    inventories: organization?.inventaires ?? false,
    googleReviews: organization?.avis_google ?? false,
    buches: organization?.buches ?? false,
  };

  const isFeatureEnabled = (feature: keyof FeatureFlags): boolean => {
    return features[feature] ?? false;
  };

  const value: OrganizationContextType = {
    user,
    profile,
    organization,
    membership,
    role: membership?.role ?? "",
    organizationId: membership?.organization_id ?? "",
    isHeadquarters: organization?.org_type === 'headquarters',
    features,
    loading: initialLoading,  // Seul le chargement initial déclenche le skeleton
    isRefreshing,
    error,
    isFeatureEnabled,
    refresh: () => loadOrganizationData(false),  // Force un rechargement complet quand appelé manuellement
  };

  // Show error screen if there's an error loading organization data
  if (error && !initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-secondary/20 to-accent/10 p-4">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              {error.isNetwork ? (
                <WifiOff className="h-5 w-5" />
              ) : (
                <AlertCircle className="h-5 w-5" />
              )}
              Erreur de connexion
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">{error.message}</p>
            <Button
              onClick={() => loadOrganizationData(false)}
              className="w-full"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Réessayer
            </Button>
            {error.isNetwork && !isOnline && (
              <p className="text-sm text-orange-600 text-center">
                Vous êtes actuellement hors ligne. La page se rechargera automatiquement quand la connexion sera rétablie.
              </p>
            )}
            {error.isNetwork && isOnline && (
              <p className="text-sm text-blue-600 text-center">
                Connexion rétablie. Cliquez sur "Réessayer" ou patientez pour un rechargement automatique.
              </p>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
